import type { EntityState } from "odyssey-shared";
import { isQuestion } from "../audio/npc-sounds.ts";
import type { DialogueSounds } from "./store.ts";

/**
 * Where an NPC is heard from: its look's sound folder and its offset from the listener in tiles.
 */
export interface NpcPlace {
  readonly sound: string;
  readonly dx: number;
  readonly dy: number;
}

/**
 * What the conversation's sound plays through: an NPC's cue and where an NPC stands.
 */
export interface NpcVoice {
  readonly cue: (sound: string, event: "greet" | "talk" | "curious" | "farewell", dx: number, dy: number, handle: number) => void;
  readonly where: (handle: number) => NpcPlace | null;
}

/**
 * The sound of a conversation with an NPC (the amendment for P2.22-T36): its greeting as the window opens, a soft blip
 * for each later line, its curious call for a line that asks a question, and its farewell as the conversation ends.
 * An NPC whose look names no sound folder, or that the view no longer holds, makes none.
 *
 * @param voice Plays an NPC's cue and says where the NPC stands.
 * @returns The sounds the dialogue window tells.
 */
export function dialogueSounds(voice: NpcVoice): DialogueSounds {
  /**
   * Plays one event for the NPC of a handle.
   * @param handle The NPC.
   * @param event The event.
   */
  const say = (handle: number, event: "greet" | "talk" | "curious" | "farewell"): void => {
    const place = voice.where(handle);
    if (place !== null) {
      voice.cue(place.sound, event, place.dx, place.dy, handle);
    }
  };
  return {
    opened: (line) => say(line.handle, "greet"),
    spoke: (line) => say(line.handle, isQuestion(line.text) ? "curious" : "talk"),
    farewell: (handle) => say(handle, "farewell"),
  };
}

/**
 * Where an NPC is heard from, read from the page's view.
 *
 * @param state The NPC's record in the view, undefined when the view lacks it.
 * @param listener The page's own character's record, undefined when the view lacks it.
 * @param soundOf The sound folder of an NPC look, by its appearance.
 * @param tile Sixteenths in a tile.
 * @returns The place, or null for something that is no NPC, one with no sound folder, or a missing record.
 */
export function npcPlaceOf(state: Pick<EntityState, "x" | "y" | "appearance"> | undefined, listener: Pick<EntityState, "x" | "y"> | undefined, soundOf: (appearance: number) => string | null, tile: number): NpcPlace | null {
  if (state === undefined || listener === undefined) {
    return null;
  }
  const sound = soundOf(state.appearance);
  return sound === null ? null : { sound, dx: (state.x - listener.x) / tile, dy: (state.y - listener.y) / tile };
}
